import { logEvent } from '@util/analytics';
import { getItem } from '@util/analytics/analytics.service';
import { getNumber } from '@util/createListingHelpers';
import { calculateShipping, getShippingRegion } from '@util/firestore/cart';
import {
  getProductByIdNotClean,
  getVariationPrice,
} from '@util/firestore/products';
import { isDomestic } from '@util/firestore/shipengine';
import { AddressDocument } from 'models/address';
import { ProductDocument, Variation } from 'models/product';
import { OfferSchemaType, StepType } from '../types';

export function getOfferStepList(
  product: ProductDocument,
  mode: 'make' | 'send' = 'make'
): { steps: StepType[]; totalSteps: number } {
  if (mode === 'send') {
    return { steps: ['offer-price'], totalSteps: 1 };
  }
  if (!product.is_flat_rate) {
    return {
      steps: [
        'offer-price',
        'shipping-payment',
        'shipping-options',
        'review-offer',
      ],
      totalSteps: 4,
    };
  }
  return {
    steps: ['offer-price', 'shipping-payment', 'review-offer'],
    totalSteps: 3,
  };
}

export const validatePrice = ({
  form,
  product,
  selectedVariations,
}: {
  form: OfferSchemaType;
  product: ProductDocument;
  selectedVariations?: Variation[] | null;
}): string[] => {
  const errors: string[] = [];
  const priceNumber = form.price;
  const askingPrice = selectedVariations?.length
    ? getVariationPrice(product, selectedVariations)
    : product.price;

  if (!priceNumber) {
    const error = `Please enter a valid price`;
    errors.push(error);
  }

  if (priceNumber < 1) {
    const error = `Please enter a price greater than $1`;
    errors.push(error);
  }

  if (priceNumber < (product.minimum_offer_price ?? 0)) {
    const error = `Your offer is lower than the buyers minimum offer price. Please try again.`;

    errors.push(error);
  }

  if (priceNumber > askingPrice) {
    const error = `Your offer is higher than the buyer's asking price. Did you mean to buy now?`;
    errors.push(error);
  }

  return errors;
};

export const validateTermsAndConditions = (form: OfferSchemaType) => {
  if (!form.termsAndConditions) {
    return [
      'Please acknowledge that this offer is binding and cannot be cancelled.',
    ];
  }
  return [];
};

export const validateShippingAddress = (form: OfferSchemaType) => {
  if (!form.shippingAddress) {
    return ['Please enter a valid shipping address.'];
  }
  return [];
};

export const validatePaymentMethod = (form: OfferSchemaType) => {
  if (!form.paymentMethodId) {
    return ['Please select a payment method.'];
  }
  return [];
};

export const validateProduct = async (
  product: ProductDocument
): Promise<
  | { success: true; product: ProductDocument }
  | { success: false; message: string }
> => {
  const currProduct = await getProductByIdNotClean(product.id);
  if (!currProduct || currProduct.out_of_stock) {
    return {
      success: false,
      message: 'This product no longer in stock.',
    };
  }
  return {
    success: true,
    product: currProduct,
  };
};

export const validateShippingOptions = ({
  form,
  product,
}: {
  form: OfferSchemaType;
  product: ProductDocument;
}) => {
  if (isDomestic(product.shipment, JSON.parse(form.shippingAddress))) {
    return [];
  }
  if (!product.is_flat_rate && !form.rate?.rate_id) {
    return ['Please select a shipping rate'];
  }
  return [];
};

export const calcShipping = ({
  form,
  product,
}: {
  form: OfferSchemaType;
  product: ProductDocument;
}) => {
  if (!form.shippingAddress) {
    throw new Error('No shipping address selected');
  }
  if (product.is_flat_rate) {
    const shippingAddress = JSON.parse(form.shippingAddress) as AddressDocument;

    const shipping = calculateShipping(
      product,
      getShippingRegion(shippingAddress)
    );

    if (!shipping) {
      throw new Error(
        'No shipping options available for this product to this address'
      );
    }

    logEvent('add_shipping_info', {
      currency: 'USD',
      items: [getItem(product)],
      shipping_tier: form.rate?.service_type ?? 'flat_rate',
    });

    return shipping.cost;
  } else {
    if (!form.rate?.rate_id) {
      throw new Error('No shipping rate selected');
    }
    return form.rate?.total_amount ?? 0;
  }
};

export function getPricePercentageOff({
  product,
  percentage,
  selectedVariations,
}: {
  product: ProductDocument;
  percentage: number;
  selectedVariations?: Variation[] | null;
}) {
  const basePrice = selectedVariations?.length
    ? getVariationPrice(product, selectedVariations)
    : product.price;

  const price = getNumber(basePrice);

  return Number((price * (1 - percentage / 100)).toFixed(2));
}
